import React from 'react';
import { Download } from 'lucide-react';
import { useAdmin } from '../context/AdminContext';
import { exportToCSV } from '../utils/adminHelpers';
import { PLAN_DETAILS } from '../constants/planDetails';
import Img from '../components/Img';
import CityFilter, { useCities, cityLabel } from '../components/CityFilter';

export default function SubscribersPage() {
  const {
    loadingSubscriptions, subscriptions,
    searchText, setSearchText, planFilter, setPlanFilter,
    subscriptionFilter, setSubscriptionFilter,
    setEditingSubscription, setEditFormData, setShowEditSubscriptionModal,
    setSelectedClient, setResetPasswordUser, setResetPasswordResult, setShowResetPasswordModal,
  } = useAdmin();
  const [cityFilter, setCityFilter] = React.useState(''); // '' = toutes les villes
  const cities = useCities();

  if (loadingSubscriptions) {
    return (
      <div className="dashboard-content">
        <h1 className="page-title">Gestion des Utilisateurs</h1>
        <div className="loading-spinner"><div className="spinner"></div><p>Chargement des abonnés...</p></div>
      </div>
    );
  }

  const filteredSubscriptions = subscriptions.filter(sub => {
    const planKey = (sub.plan_type || sub.plan_name || '').toLowerCase();
    const clientName = (sub.user?.name || '').toLowerCase();
    const clientPhone = (sub.user?.phone || '').toLowerCase();
    const clientEmail = (sub.user?.email || '').toLowerCase();
    const searchLower = searchText.toLowerCase();
    const matchesSearch = !searchText || clientName.includes(searchLower) || clientPhone.includes(searchLower) || clientEmail.includes(searchLower);
    const matchesPlan = planFilter === 'all' || planKey === planFilter || (sub.status === 'no_subscription' && planFilter !== 'all');
    const matchesStatus = subscriptionFilter === 'all' || sub.status === subscriptionFilter;
    const matchesCity = !cityFilter || sub.user?.city_slug === cityFilter;
    return matchesSearch && matchesPlan && matchesStatus && matchesCity;
  });

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <h1 className="page-title">Gestion des Utilisateurs</h1>
        <div className="page-actions" style={{ gap: '1rem', display: 'flex', alignItems: 'center' }}>
          <input type="text" placeholder="Rechercher par nom, téléphone ou email..." value={searchText} onChange={(e) => setSearchText(e.target.value)}
            style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #E9EBEF', fontSize: '0.875rem', flex: 1, maxWidth: '300px' }} />
          <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}
            style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #E9EBEF', fontSize: '0.875rem', background: 'white' }}>
            <option value="all">Tous les plans</option>
            <option value="basic">BASIQUE</option>
            <option value="private-basic">PRIVÉ BASIQUE</option>
            <option value="standard">STANDARD</option>
            <option value="premium">PREMIUM</option>
            <option value="university">UNIVERSITÉ</option>
            <option value="elite">ÉLITE</option>
          </select>
          <select value={subscriptionFilter} onChange={(e) => setSubscriptionFilter(e.target.value)}
            style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #E9EBEF', fontSize: '0.875rem', background: 'white' }}>
            <option value="all">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="expired">Expiré</option>
            <option value="no_subscription">Pas d'abonnement</option>
          </select>
          <CityFilter value={cityFilter} onChange={setCityFilter} cities={cities} className=""
            style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #E9EBEF', fontSize: '0.875rem', background: 'white' }} />
          <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
            onClick={() => exportToCSV('abonnes', filteredSubscriptions.map(s => ({
              Nom: s.client_name || s.name || '—', Téléphone: s.client_phone || s.phone || '—',
              Email: s.client_email || s.email || '—', Ville: cityLabel(cities, s.user?.city_slug), Plan: s.plan_type || s.plan_name || '—',
              Prix: s.price || '—', Expiration: s.valid_until ? new Date(s.valid_until).toLocaleDateString('fr-FR') : '—',
              Statut: s.status || '—',
            })))}
          >
            <Download size={16} /> Exporter CSV
          </button>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', marginTop: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#FFFFFF', borderBottom: '2px solid #E9EBEF' }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '700', color: '#414852', fontSize: '0.875rem', textTransform: 'uppercase' }}>Nom</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '700', color: '#414852', fontSize: '0.875rem', textTransform: 'uppercase' }}>Téléphone</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '700', color: '#414852', fontSize: '0.875rem', textTransform: 'uppercase' }}>Email</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '700', color: '#414852', fontSize: '0.875rem', textTransform: 'uppercase' }}>Ville</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '700', color: '#414852', fontSize: '0.875rem', textTransform: 'uppercase' }}>Plan</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '700', color: '#414852', fontSize: '0.875rem', textTransform: 'uppercase' }}>Prix</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '700', color: '#414852', fontSize: '0.875rem', textTransform: 'uppercase' }}>Date d'expiration</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#414852', fontSize: '0.875rem', textTransform: 'uppercase' }}>Statut</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#414852', fontSize: '0.875rem', textTransform: 'uppercase' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscriptions.map((sub, idx) => {
                const planKey = (sub.plan_type || sub.plan_name || '').toLowerCase();
                const plan = PLAN_DETAILS[planKey] || { name: sub.plan_name || 'INCONNU', color: '#5C636E' };
                return (
                  <tr key={sub.subscription_id} style={{ borderBottom: '1px solid #E9EBEF', background: idx % 2 === 0 ? '#ffffff' : '#FFFFFF' }}>
                    <td style={{ padding: '1rem', color: '#1E2229', fontWeight: '500' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }} onClick={() => setSelectedClient(sub)}>
                        {sub.user?.photo ? (
                          <Img src={sub.user.photo} alt="Profile" style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: plan.color, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '0.875rem' }}>
                            {(sub.user?.name || '?').charAt(0)}
                          </div>
                        )}
                        <span style={{ textDecoration: 'underline', color: '#C9350F', fontWeight: '600' }}>{sub.user?.name || 'Inconnu'}</span>
                      </div>
                    </td>
                    <td style={{ padding: '1rem', color: '#5C636E' }}>{sub.user?.phone || '-'}</td>
                    <td style={{ padding: '1rem', color: '#5C636E' }}>{sub.user?.email || '-'}</td>
                    <td style={{ padding: '1rem', color: '#5C636E' }}>{cityLabel(cities, sub.user?.city_slug)}</td>
                    <td style={{ padding: '1rem' }}>
                      {sub.status === 'no_subscription' ? (
                        <span style={{ color: '#9AA0A8', fontSize: '0.875rem', fontWeight: '500' }}>-</span>
                      ) : (
                        <span style={{ background: `${plan.color}20`, color: plan.color, padding: '0.35rem 0.75rem', borderRadius: '6px', fontSize: '0.875rem', fontWeight: '600' }}>{plan.name}</span>
                      )}
                    </td>
                    <td style={{ padding: '1rem', color: sub.status === 'no_subscription' ? '#5C636E' : plan.color, fontWeight: sub.status === 'no_subscription' ? '400' : '600' }}>
                      {sub.price ? `${sub.price.toLocaleString()} FC` : '-'}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {sub.valid_until ? (() => {
                        const expirationDate = new Date(sub.valid_until);
                        const now = new Date();
                        const daysLeft = Math.ceil((expirationDate - now) / (1000 * 60 * 60 * 24));
                        const isExpired = now > expirationDate;
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <span style={{ fontSize: '0.875rem', color: '#1E2229', fontWeight: '500' }}>{expirationDate.toLocaleDateString('fr-FR', { year: '2-digit', month: '2-digit', day: '2-digit' })}</span>
                            <span style={{ fontSize: '0.75rem', color: isExpired ? '#dc2626' : daysLeft <= 7 ? '#FFB900' : '#10b981', fontWeight: '600', display: isExpired ? 'block' : (daysLeft <= 7 ? 'block' : 'none') }}>
                              {isExpired ? '❌ Expiré' : `⚠️ ${daysLeft} jour${daysLeft > 1 ? 's' : ''}`}
                            </span>
                          </div>
                        );
                      })() : '-'}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <span style={{ background: sub.status === 'no_subscription' ? '#F4F5F7' : (sub.status === 'active' ? '#ECFDF5' : '#fee2e2'), color: sub.status === 'no_subscription' ? '#5C636E' : (sub.status === 'active' ? '#166534' : '#991b1b'), padding: '0.35rem 0.75rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>
                        {sub.status === 'no_subscription' ? "❌ Pas d'abonnement" : (sub.status === 'active' ? '✅ Actif' : '⏱️ Expiré')}
                      </span>
                      {sub.user?.status === 'suspended' || sub.user?.status === 'banned' ? (
                        <div style={{ marginTop: '0.35rem' }}>
                          <span title={sub.user?.status_reason || ''} style={{ background: sub.user.status === 'banned' ? '#FEE2E2' : '#FFF7E6', color: sub.user.status === 'banned' ? '#B91C1C' : '#B45309', padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase' }}>
                            {sub.user.status === 'banned' ? 'Compte banni' : 'Compte suspendu'}
                          </span>
                        </div>
                      ) : null}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                      <button onClick={() => {
                        setEditingSubscription(sub);
                        const statusValue = sub.status === 'no_subscription' ? 'active' : (sub.status || 'active');
                        setEditFormData({ plan_type: sub.plan_type || '', plan_name: sub.plan_name || '', valid_until: sub.valid_until ? sub.valid_until.split('T')[0] : '', status: statusValue, client_name: sub.user?.name || '', client_phone: sub.user?.phone || '', client_email: sub.user?.email || '' });
                        setShowEditSubscriptionModal(true);
                      }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', padding: '0.5rem', color: '#C9350F' }} title="Modifier l'abonnement">✏️</button>
                      <button onClick={() => { setResetPasswordUser(sub.user); setResetPasswordResult(null); setShowResetPasswordModal(true); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', padding: '0.5rem', color: '#dc2626' }} title="Réinitialiser le mot de passe">🔑</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredSubscriptions.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#5C636E' }}><p style={{ fontSize: '1.1rem' }}>Aucun abonné trouvé</p></div>
        )}
        {subscriptions.length > 0 && (
          <div style={{ padding: '1rem', background: '#FFFFFF', borderTop: '1px solid #E9EBEF', fontSize: '0.875rem', color: '#5C636E', textAlign: 'right' }}>
            {filteredSubscriptions.length !== subscriptions.length && <><strong>{filteredSubscriptions.length}</strong> affiché{filteredSubscriptions.length > 1 ? 's' : ''} · </>}
            <strong>{subscriptions.length}</strong> abonné{subscriptions.length > 1 ? 's' : ''} au total
          </div>
        )}
      </div>
    </div>
  );
}
